import { useEffect } from 'react';
import { useClerk } from '@clerk/clerk-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Smart authentication flow that handles OAuth errors gracefully
 * by redirecting users to appropriate sign-up/sign-in flows
 */
export default function SmartAuthFlow({
  children,
}: {
  children: React.ReactNode;
}) {
  const clerk = useClerk();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleAuthFlow = () => {
      const url = window.location.href;
      const pathname = window.location.pathname;

      // Check if we're on a problematic callback path that leads to 404
      const isProblematicCallback =
        pathname.includes('/auth/sso-callback') ||
        pathname.includes('/sso-callback') ||
        url.includes('NOT_FOUND') ||
        url.includes('404');

      if (isProblematicCallback) {
        console.log(
          'Detected problematic OAuth callback, redirecting to auth flow'
        );

        // Check if this is a new user scenario (External Account not found)
        const errorDescription = searchParams.get('error_description') || '';
        const isNewUser =
          errorDescription.includes('External Account') ||
          errorDescription.includes('not found') ||
          url.includes('NOT_FOUND');

        if (isNewUser) {
          // Redirect to sign-up with a helpful message
          navigate('/sign-up?from=oauth&provider=google', {
            replace: true,
          });
        } else {
          // Redirect to sign-in for other errors
          navigate('/sign-in?from=oauth', { replace: true });
        }
      }
    };

    // Only run if Clerk is loaded
    if (clerk.loaded) {
      handleAuthFlow();
    }
  }, [clerk.loaded, navigate, searchParams]);

  return <>{children}</>;
}
